import {AKComponent} from '@theme/component'
import {FilterUpdateEvent, FilterContentMoveEvent, DialogCloseEvent, ThemeEvents, FloatingPanelOpenedEvent} from '@theme/events'
import {startViewTransition, formatMoney, debounce} from '@theme/utilities'
import {sectionRenderer} from '@theme/section-renderer'

/**
 * Configuration constants
 */
const CONFIG = {
    SEARCH_QUERY: 'q',
    DIALOG_ANIMATION_SPEED: 300,
    RESIZE_DEBOUNCE_DELAY: 250,
    PRICE_FILTERS: ['filter.v.price.gte', 'filter.v.price.lte'],
    NON_FILTER_PARAMS: ['page', 'sort_by', 'q'],
};

/**
 * Utility functions
 */
const Utils = {
    /**
     * Get section ID from any element within a Shopify section
     * @param {Element} element - Element within the section
     * @returns {string} The section ID
     */
    getSectionId(element) {
        const section = element.closest('.shopify-section');
        const id = section?.id;

        if (!id) {
            console.error('Element must be within a .shopify-section');
            return '';
        }

        return id;
    },

    /**
     * Checks if a parameter is a filter parameter
     * @param {string} key - The parameter key
     * @returns {boolean}
     */
    isFilterParam(key) {
        return key.startsWith('filter.') && !CONFIG.NON_FILTER_PARAMS.includes(key);
    },

    /**
     * Checks if a parameter is a price filter
     * @param {string} key - The parameter key
     * @returns {boolean}
     */
    isPriceFilter(key) {
        return key.includes('price.gte') || key.includes('price.lte');
    },

    /**
     * Gets the closest facets form component
     * @param {Element} element - The starting element
     * @returns {FacetsFormComponent|null}
     */
    getFacetsForm(element) {
        const facetsForm = element.closest('facets-form-component') ||
            element.closest('.shopify-section')?.querySelector('facets-form-component');

        return facetsForm instanceof FacetsFormComponent ? facetsForm : null;
    }
};

/**
 * Component for handling filter form interactions and URL management
 */
class FacetsFormComponent extends AKComponent {
    /**
     * Creates URL parameters from form data
     * @param {FormData} [formData] - Optional form data to use instead of the main form
     * @returns {URLSearchParams} The processed URL parameters
     */
    createURLParameters(formData = null) {
        if (!formData && !this.refs.facetsForm) {
            return new URLSearchParams();
        }

        const data = formData || new FormData(this.refs.facetsForm);
        const newParameters = new URLSearchParams(/** @type any */ (data));

        // Clean up empty price filters
        this._cleanupPriceFilters(newParameters);

        // Remove page parameter to reset pagination
        newParameters.delete('page');

        // Preserve search query
        const searchQuery = this._getSearchQuery();
        if (searchQuery) {
            newParameters.set(CONFIG.SEARCH_QUERY, searchQuery);
        }

        return newParameters;
    }

    /**
     * Clean up empty price filter parameters
     * @param {URLSearchParams} parameters
     * @private
     */
    _cleanupPriceFilters(parameters) {
        CONFIG.PRICE_FILTERS.forEach(filter => {
            if (parameters.get(filter) === '') {
                parameters.delete(filter);
            }
        });
    }

    /**
     * Gets the search query parameter from the current URL
     * @returns {string} The search query
     * @private
     */
    _getSearchQuery() {
        const url = new URL(window.location.href);
        return url.searchParams.get(CONFIG.SEARCH_QUERY) ?? '';
    }

    /**
     * Get the section ID from the component attribute
     * @returns {string} The section ID
     */
    get sectionId() {
        const id = this.getAttribute('section-id');
        if (!id) {
            console.error('FacetsFormComponent: Section ID is required but not found');
            return '';
        }
        return id;
    }

    /**
     * Updates the URL with current filter parameters
     * @private
     */
    _updateURLHash() {
        const url = new URL(window.location.href);
        const urlParameters = this.createURLParameters();

        // Clear existing search parameters
        url.search = '';

        // Add new parameters
        urlParameters.forEach((value, param) => {
            url.searchParams.append(param, value);
        });

        // Update browser history
        history.pushState(
            {urlParameters: urlParameters.toString()},
            '',
            url.toString()
        );
    }

    /**
     * Checks if the filters dialog containing this form is currently open
     * @returns {boolean} True if the dialog is open
     * @private
     */
    _isDialogOpen() {
        const panel = this.closest('filters-dialog-component')?.querySelector('.c-filters__panel');
        return !!panel?.hasAttribute('open');
    }

    /**
     * Updates filters and renders the section
     */
    updateFilters = () => {
        // Dispatch filter update event with filter count (for UI updates like badge count)
        const urlParams = this.createURLParameters();
        const filterCount = this.getActiveFilterCount(urlParams);
        const shouldShowClear = this.shouldShowClearAll(urlParams);
        const filterEvent = new FilterUpdateEvent(urlParams, this.sectionId, {filterCount, shouldShowClear});
        this.dispatchEvent(filterEvent);

        // Apply filters immediately, even inside the dialog, so the UI stays in sync
        this._updateURLHash();
        this._updateSection();
    };

    /**
     * Counts the number of active filters based on URL parameters
     * @param {URLSearchParams} params - The URL parameters to analyze
     * @returns {number} The number of active filters
     */
    getActiveFilterCount(params) {
        let count = 0;
        let priceFilterCounted = false;

        for (const [key, value] of params.entries()) {
            // Skip non-filter parameters
            if (CONFIG.NON_FILTER_PARAMS.includes(key)) {
                continue;
            }

            // Count filter parameters
            if (Utils.isFilterParam(key)) {
                // Price range filters - count as one filter if either min or max is set
                if (Utils.isPriceFilter(key)) {
                    if (!priceFilterCounted) {
                        const hasMinPrice = params.has('filter.v.price.gte') && params.get('filter.v.price.gte') !== '';
                        const hasMaxPrice = params.has('filter.v.price.lte') && params.get('filter.v.price.lte') !== '';

                        if (hasMinPrice || hasMaxPrice) {
                            count++;
                            priceFilterCounted = true;
                        }
                    }
                } else {
                    // Other filters - count each value
                    count++;
                }
            }
        }

        return count;
    }

    /**
     * Determines if the clear all button should be shown
     * @param {URLSearchParams} params - The URL parameters to analyze
     * @returns {boolean} True if there are active filters
     */
    shouldShowClearAll(params) {
        return this.getActiveFilterCount(params) > 0;
    }

    /**
     * Updates the section content
     * @private
     */
    _updateSection() {
        const sectionId = this.sectionId;
        if (!sectionId) return;

        const useViewTransition = !this.closest('dialog') && !this._isDialogOpen();

        if (useViewTransition && typeof startViewTransition === 'function') {
            startViewTransition(
                () => sectionRenderer.renderSection(sectionId),
                ['product-grid']
            );
        } else {
            sectionRenderer.renderSection(sectionId);
        }
    }

    /**
     * Updates filters based on a provided URL
     * @param {string} url - The URL to update filters with
     */
    updateFiltersByURL(url) {
        if (!url || typeof url !== 'string') {
            console.error('FacetsFormComponent: Invalid URL provided');
            return;
        }

        history.pushState('', '', url);

        const urlParams = this.createURLParameters();
        const filterCount = this.getActiveFilterCount(urlParams);
        const shouldShowClear = this.shouldShowClearAll(urlParams);
        const filterEvent = new FilterUpdateEvent(urlParams, this.sectionId, {filterCount, shouldShowClear});
        this.dispatchEvent(filterEvent);

        this._updateSection();
    }
}

/**
 * Component for handling individual filter input interactions
 */
class FacetInputsComponent extends AKComponent {
    /**
     * Get the section ID from the closest Shopify section
     * @returns {string} The section ID
     */
    get sectionId() {
        return Utils.getSectionId(this);
    }

    /**
     * Updates filters through the parent form component
     */
    updateFilters() {
        const facetsForm = Utils.getFacetsForm(this);

        if (!facetsForm) {
            console.error('FacetInputsComponent: No parent facets-form-component found');
            return;
        }

        facetsForm.updateFilters();
    }

    /**
     * Handles keyboard navigation for filter inputs
     * @param {KeyboardEvent} event - The keydown event
     */
    handleKeyDown(event) {
        if (!(event.target instanceof HTMLElement)) return;

        const input = event.target.querySelector('input');
        if (!(input instanceof HTMLInputElement)) return;

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            input.checked = !input.checked;
            this.updateFilters();
        }
    }
}

class PriceFacetComponent extends AKComponent {
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('keydown', this._onKeyDown);

        // Set up toggle listener on parent details to dispatch floating panel opened event
        const parentDetails = this.closest('details');
        if (parentDetails) {
            this.toggleListener = this._onToggle.bind(this);
            parentDetails.addEventListener('toggle', this.toggleListener);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('keydown', this._onKeyDown);

        if (this.toggleListener) {
            const parentDetails = this.closest('details');
            if (parentDetails) {
                parentDetails.removeEventListener('toggle', this.toggleListener);
            }
            this.toggleListener = null;
        }
    }

    /**
     * Handles keydown events to restrict input to valid characters
     * @param {KeyboardEvent} event - The keydown event
     */
    _onKeyDown = (event) => {
        if (event.metaKey) return;

        const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
        if (!event.key.match(pattern)) event.preventDefault();
    };

    /**
     * Updates price filter and results
     */
    updatePriceFilterAndResults() {
        const {minInput, maxInput} = this.refs;

        this._adjustToValidValues(minInput);
        this._adjustToValidValues(maxInput);

        const facetsForm = Utils.getFacetsForm(this);
        if (!facetsForm) return;

        facetsForm.updateFilters();
        this._setMinAndMaxValues();

        // Close the floating panel parent (same behavior as floating-panel.js)
        this._closeFloatingPanel();
    }

    /**
     * Handles toggle events on parent details element
     * Dispatches floating panel opened event when opened
     * @private
     */
    _onToggle = () => {
        const parentDetails = this.closest('details');

        // If this details is being opened, dispatch event
        if (parentDetails && parentDetails.open) {
            // Use requestAnimationFrame to ensure DOM is updated before dispatching
            requestAnimationFrame(() => {
                document.dispatchEvent(new FloatingPanelOpenedEvent(parentDetails));
            });
        }
    };

    /**
     * Closes the parent floating panel (details element) after filter update
     * This mimics the behavior from FloatingPanelComponent
     * @private
     */
    _closeFloatingPanel() {
        const parentDetails = this.closest('details');
        if (parentDetails && parentDetails.open) {
            parentDetails.removeAttribute('open');

            // Return focus to the summary element
            const summary = parentDetails.querySelector('summary');
            if (summary) {
                summary.focus();
            }
        }
    }

    /**
     * Adjusts input values to be within valid range
     * @param {HTMLInputElement} input - The input element to adjust
     */
    _adjustToValidValues(input) {
        if (input.value.trim() === '') return;

        const value = Number(input.value);
        const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
        const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

        if (value < min) input.value = min.toString();
        if (value > max) input.value = max.toString();
    }

    /**
     * Sets min and max values for the inputs
     */
    _setMinAndMaxValues() {
        const {minInput, maxInput} = this.refs;

        if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
        if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
        if (minInput.value === '') maxInput.setAttribute('data-min', '0');
        if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
    }

}

class FacetClearComponent extends AKComponent {
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('keyup', this._handleKeyUp);
        document.addEventListener(ThemeEvents.filterUpdate, this._handleFilterUpdate);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener(ThemeEvents.filterUpdate, this._handleFilterUpdate);
    }

    /**
     * Clears the filter
     * @param {Event} event - The click event
     */
    clearFilter(event) {
        if (!(event.target instanceof HTMLElement)) return;

        if (event instanceof KeyboardEvent) {
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }
            event.preventDefault();
        }

        const facetsForm = Utils.getFacetsForm(this);
        if (!facetsForm) return;

        facetsForm.querySelectorAll('.c-filters__content input').forEach((input) => {
            if (!(input instanceof HTMLInputElement)) return;

            // Keep checkbox values intact (they are the filter values), only reset text inputs
            if (input.type === 'checkbox') {
                input.checked = false;
            } else {
                input.value = '';
            }
        });

        facetsForm.updateFilters();
    }

    /**
     * Handles keyup events
     * @param {KeyboardEvent} event - The keyup event
     */
    _handleKeyUp = (event) => {
        if (event.metaKey) return;
        if (event.key === 'Enter') this.clearFilter(event);
    };

    /**
     * Toggle clear button visibility and update filter count when filters are applied.
     * Happens before the Section Rendering Request resolves.
     *
     * @param {FilterUpdateEvent} event
     */
    _handleFilterUpdate = (event) => {
        const {shouldShowClear = false, filterCount} = event.detail.data ?? {};

        // Apply the active state directly to the component itself
        this.classList.toggle('c-facets__clear--active', shouldShowClear);

        // Update the count right away, before the section re-render resolves
        if (this.refs.filterCount && typeof filterCount === 'number') {
            this.refs.filterCount.textContent = String(filterCount);
        }
    };
}

/**
 * Component for handling sorting dropdown with keyboard navigation
 */
class SortingFilterComponent extends AKComponent {
    requiredRefs = ['details', 'listbox'];

    /**
     * Handles keyboard navigation in the sorting dropdown
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleKeyDown = (event) => {
        const {listbox} = this.refs;
        if (!(listbox instanceof Element)) return;

        const options = Array.from(listbox.querySelectorAll('[role="option"]'));
        const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
        let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
                this._moveFocus(options, newFocusIndex);
                break;

            case 'ArrowUp':
                event.preventDefault();
                newFocusIndex = Math.max(newFocusIndex - 1, 0);
                this._moveFocus(options, newFocusIndex);
                break;

            case 'Enter':
            case ' ':
                if (event.target instanceof Element) {
                    const targetOption = event.target.closest('[role="option"]');
                    if (targetOption) {
                        event.preventDefault();
                        this._selectOption(targetOption);
                    }
                }
                break;

            case 'Escape':
                event.preventDefault();
                this._closeDropdown();
                break;
        }
    };

    /**
     * Handles details toggle event
     */
    handleToggle = () => {
        const {details, listbox} = this.refs;
        if (!(details instanceof HTMLDetailsElement)) return;

        const isOpen = details.open;

        if (isOpen && listbox instanceof Element) {
            // Move focus to selected option when dropdown opens
            const selectedOption = listbox.querySelector('[aria-selected="true"]');
            if (selectedOption instanceof HTMLElement) {
                selectedOption.focus();
            }
        }
    };

    /**
     * Moves focus between options
     * @param {Element[]} options - The option elements
     * @param {number} newIndex - The index of the option to focus
     * @private
     */
    _moveFocus(options, newIndex) {
        // Remove tabindex from all options
        options.forEach((option) => {
            if (option instanceof HTMLElement) {
                option.tabIndex = -1;
            }
        });

        // Set tabindex and focus on new option
        const targetOption = options[newIndex];
        if (targetOption instanceof HTMLElement) {
            targetOption.tabIndex = 0;
            targetOption.focus();
        }
    }

    /**
     * Selects an option and triggers form submission
     * @param {Element} option - The option element to select
     * @private
     */
    _selectOption(option) {
        const input = option.querySelector('input[type="radio"]');
        if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
            // Update aria-selected states
            this.querySelectorAll('[role="option"]').forEach((opt) => {
                opt.setAttribute('aria-selected', 'false');
            });
            option.setAttribute('aria-selected', 'true');

            // Trigger click on the input to ensure normal form behavior
            input.click();

            // Close dropdown and return focus (handles tabIndex reset)
            this._closeDropdown();
        }
    }

    /**
     * Closes the dropdown and returns focus to summary
     * @private
     */
    _closeDropdown() {
        const {details} = this.refs;
        if (details instanceof HTMLDetailsElement) {
            // Reset focus to match the actual selected option
            const options = this.querySelectorAll('[role="option"]');
            const selectedOption = this.querySelector('[aria-selected="true"]');

            options.forEach((opt) => {
                if (opt instanceof HTMLElement) {
                    opt.tabIndex = -1;
                }
            });

            if (selectedOption instanceof HTMLElement) {
                selectedOption.tabIndex = 0;
            }

            details.open = false;
        }
    }

    /**
     * Updates filter and sorting
     * @param {Event} event - The change event
     */
    updateFilterAndSorting(event) {
        const facetsForm = Utils.getFacetsForm(this);
        if (!facetsForm) return;

        facetsForm.updateFilters();
        this._closeDropdown();
    }
}


/**
 * FiltersDialogComponent - Manages filter dialog for all layout modes
 * Replaces both old facets-dialog and filters-dialog-component
 * Handles drawer, horizontal, and vertical layouts
 */
class FiltersDialogComponent extends AKComponent {
    static CSS_CLASSES = {
        DIALOG: 'c-facets-dialog',
        MODAL: 'c-dialog-modal',
        CLOSING: 'c-dialog-closing',
        BACKDROP: 'c-facets-dialog__backdrop',
        BACKDROP_HIDDEN: 'c-facets-dialog__backdrop--hidden',
        PANEL: '.c-filters__panel'
    };

    constructor() {
        super();
        this.boundHandleClick = this.handleClick.bind(this);
        this.boundHandleKeyDown = this.handleKeyDown.bind(this);
        this.boundHandleGlobalResize = this.handleGlobalResize.bind(this);
        this.resizeTimeout = null;
        this.dialog = null;
    }

    connectedCallback() {
        super.connectedCallback();
        this._initializeDialog();
        this._addEventListeners();
    }

    /**
     * Initialize dialog element
     * @private
     */
    _initializeDialog() {
        this.dialog = this.querySelector(FiltersDialogComponent.CSS_CLASSES.PANEL);

        if (!this.dialog) {
            console.error('FiltersDialogComponent: Missing dialog element');
            return;
        }
    }

    /**
     * Add event listeners
     * @private
     */
    _addEventListeners() {
        window.addEventListener('resize', this.boundHandleGlobalResize);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._removeEventListeners();
        this._cleanup();
    }

    /**
     * Remove event listeners
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener('click', this.boundHandleClick, true);
        document.removeEventListener('keydown', this.boundHandleKeyDown, true);
        window.removeEventListener('resize', this.boundHandleGlobalResize);
    }

    /**
     * Cleanup resources
     * @private
     */
    _cleanup() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
        }
    }

    /**
     * Shows the dialog immediately without animation.
     */
    showDialog() {
        if (!this.dialog) return;

        this.simulateDialogOpen();

        setTimeout(() => {
            document.addEventListener('click', this.boundHandleClick, true);
            document.addEventListener('keydown', this.boundHandleKeyDown, true);
        });
    }

    /**
     * Simulates opening the dialog by applying CSS classes
     */
    simulateDialogOpen() {
        if (!this.dialog) return;

        // Add dialog simulation classes and set open state
        this.dialog.classList.add(
            FiltersDialogComponent.CSS_CLASSES.DIALOG,
            FiltersDialogComponent.CSS_CLASSES.MODAL
        );
        this.dialog.setAttribute('open', '');

        // Manage backdrop
        this._showBackdrop();

        // Set focus trap and other modal behaviors
        this.dialog.focus();
    }

    /**
     * Show or create backdrop
     * @private
     */
    _showBackdrop() {
        let backdrop = this._findBackdrop();

        if (!backdrop) {
            backdrop = this._createBackdrop();
        }

        backdrop.classList.remove(FiltersDialogComponent.CSS_CLASSES.BACKDROP_HIDDEN);
    }

    /**
     * Find existing backdrop
     * @private
     * @returns {Element|null}
     */
    _findBackdrop() {
        return this.dialog.parentNode.querySelector(`.${FiltersDialogComponent.CSS_CLASSES.BACKDROP}`);
    }

    /**
     * Create new backdrop
     * @private
     * @returns {Element}
     */
    _createBackdrop() {
        const backdrop = document.createElement('div');
        backdrop.className = FiltersDialogComponent.CSS_CLASSES.BACKDROP;
        this.dialog.parentNode.insertBefore(backdrop, this.dialog.nextSibling);
        return backdrop;
    }

    /**
     * Simulates closing the dialog by removing CSS classes
     */
    simulateDialogClose() {
        if (!this.dialog) return;

        // Remove dialog classes for all modes (unified treatment)
        this.dialog.classList.remove(
            FiltersDialogComponent.CSS_CLASSES.DIALOG,
            FiltersDialogComponent.CSS_CLASSES.MODAL
        );
        this.dialog.removeAttribute('open');

        // Hide backdrop
        this._hideBackdrop();
    }

    /**
     * Hide backdrop
     * @private
     */
    _hideBackdrop() {
        const backdrop = this._findBackdrop();
        if (backdrop) {
            backdrop.classList.add(FiltersDialogComponent.CSS_CLASSES.BACKDROP_HIDDEN);
        }
    }

    /**
     * Closes the dialog with animation.
     * @param {string} closeReason - The reason for closing (escape, outside-click, button)
     */
    closeDialog = (closeReason = 'button') => {
        if (!this.dialog || !this.isDialogOpen()) return;

        // Remove event listeners
        document.removeEventListener('click', this.boundHandleClick, true);
        document.removeEventListener('keydown', this.boundHandleKeyDown, true);

        // Dispatch close event
        document.dispatchEvent(new DialogCloseEvent(this.id, {closeReason}));

        // Start closing animation
        this._startClosingAnimation();
    };

    /**
     * Start closing animation
     * @private
     */
    _startClosingAnimation() {
        this.dialog.classList.add(FiltersDialogComponent.CSS_CLASSES.CLOSING);

        // Wait for animation to complete before actually closing
        const animationDuration = this._getAnimationDuration();
        setTimeout(() => {
            this._finishClosing();
        }, animationDuration);
    }

    /**
     * Get animation duration from CSS custom property
     * @private
     * @returns {number} Duration in milliseconds
     */
    _getAnimationDuration() {
        const speed = getComputedStyle(this.dialog).getPropertyValue('--ak-dialog-animation-speed');
        return parseFloat(speed) * 1000 || CONFIG.DIALOG_ANIMATION_SPEED;
    }

    /**
     * Finish closing animation and cleanup
     * @private
     */
    _finishClosing() {
        this.dialog.classList.remove(FiltersDialogComponent.CSS_CLASSES.CLOSING);
        this.simulateDialogClose();
    }

    /**
     * Checks if the dialog is currently open
     * @returns {boolean} True if dialog is open
     */
    isDialogOpen() {
        return this.dialog && this.dialog.hasAttribute('open');
    }

    /**
     * Toggles the dialog.
     */
    toggleDialog = () => {
        if (this.isDialogOpen()) {
            this.closeDialog();
        } else {
            this.showDialog();
        }
    };

    /**
     * Closes the dialog when the user clicks outside of it.
     * @param {MouseEvent} event - The mouse event.
     */
    handleClick(event) {
        const isBackdropClick = event.target === this.dialog ||
            event.target.classList.contains(FiltersDialogComponent.CSS_CLASSES.BACKDROP);

        if (isBackdropClick) {
            event.preventDefault();
            this.closeDialog('outside-click');
        }
    }

    /**
     * Closes the dialog when the user presses the escape key.
     *
     * @param {KeyboardEvent} event - The keyboard event.
     */
    handleKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeDialog('escape');
        }
    }

    /**
     * Handles global window resize to reset dialog state
     * Uses debounce from utilities.js to avoid excessive calls
     */
    handleGlobalResize = debounce(() => {
        if (this.isDialogOpen()) {
            this._resetDialogState();
        }
    }, CONFIG.RESIZE_DEBOUNCE_DELAY);

    /**
     * Reset dialog state on resize
     * @private
     */
    _resetDialogState() {
        this.closeDialog('resize');

        // Reset classes for all modes (unified treatment)
        this.dialog.classList.remove(
            FiltersDialogComponent.CSS_CLASSES.DIALOG,
            FiltersDialogComponent.CSS_CLASSES.CLOSING
        );
        this.dialog.removeAttribute('open');

        // Hide backdrop
        this._hideBackdrop();
    }


}

/**
 * FacetLinkComponent - Handles pagination and filter removal links
 * Intercepts clicks and uses Section Rendering API instead of full page reload
 */
class FacetLinkComponent extends AKComponent {
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this.handleClick);
    }

    /**
     * Handles click events on facet links
     * @param {MouseEvent} event - The click event
     */
    handleClick = (event) => {
        // Find the actual anchor element that was clicked
        const anchor = event.target.closest('a');

        if (!anchor) return;

        // Prevent default navigation
        event.preventDefault();

        // Get current URL (contains filters added via JavaScript)
        const currentUrl = new URL(window.location.href);

        // Create new URL parameters by combining current filters with new page
        const newParams = new URLSearchParams(currentUrl.search);

        // Get the page parameter from the clicked link
        // Handle empty href (page 1) or href pointing to current page
        let newPage = null;

        if (anchor.href && anchor.href !== '' && anchor.href !== window.location.href) {
            try {
                const clickedUrl = new URL(anchor.href);
                newPage = clickedUrl.searchParams.get('page');
            } catch (e) {
                console.warn('FacetLinkComponent: Invalid href, treating as page 1', e);
            }
        }
        // If href is empty or invalid, treat as page 1 (no page parameter)

        // Update or remove page parameter
        if (newPage) {
            newParams.set('page', newPage);
        } else {
            newParams.delete('page');
        }

        // Build the final URL
        const finalUrl = new URL(currentUrl.pathname, window.location.origin);
        finalUrl.search = newParams.toString();

        // Get section ID
        const sectionId = this.getSectionId();
        if (!sectionId) {
            console.error('FacetLinkComponent: Could not find section ID');
            // Fallback to normal navigation
            window.location.href = finalUrl.toString();
            return;
        }

        // Update browser URL
        history.pushState({}, '', finalUrl.toString());

        // Dispatch filter update event
        this.dispatchEvent(new FilterUpdateEvent(newParams, sectionId));

        // Render the section using Section Rendering API with explicit URL
        // Use requestAnimationFrame to ensure URL is updated before fetching
        requestAnimationFrame(() => {
            sectionRenderer.renderSection(sectionId, { cache: false });
        });
    };

    /**
     * Get the section ID from the closest shopify section
     * @returns {string} The section ID without the 'shopify-section-' prefix
     */
    getSectionId() {
        // First try to get it from a parent facets-form-component which has the normalized ID
        const facetsForm = this.closest('facets-form-component');
        if (facetsForm && facetsForm.getAttribute('section-id')) {
            return facetsForm.getAttribute('section-id');
        }

        // Otherwise, get it from the section element and normalize it
        const section = this.closest('.shopify-section');
        if (!section?.id) return '';

        // Remove the 'shopify-section-' prefix
        return section.id.replace(/^shopify-section-/, '');
    }
}

/**
 * Register custom elements if not already defined
 */

if (!customElements.get('facets-form-component')) {
    customElements.define('facets-form-component', FacetsFormComponent);
}

if (!customElements.get('facet-inputs-component')) {
    customElements.define('facet-inputs-component', FacetInputsComponent);
}

if (!customElements.get('price-facet-component')) {
    customElements.define('price-facet-component', PriceFacetComponent);
}

if (!customElements.get('facet-clear-component')) {
    customElements.define('facet-clear-component', FacetClearComponent);
}

if (!customElements.get('sorting-filter-component')) {
    customElements.define('sorting-filter-component', SortingFilterComponent);
}

if (!customElements.get('filters-dialog-component')) {
    customElements.define('filters-dialog-component', FiltersDialogComponent);
}

if (!customElements.get('facet-link')) {
    customElements.define('facet-link', FacetLinkComponent);
}

